import { Component, Input, ChangeDetectionStrategy, ViewChild, ElementRef, AfterViewInit, OnChanges, SimpleChanges, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { GoogleMapsLoaderService } from '../../../services/google-maps-loader.service';
import { GoogleMapsService } from '../../../services/third-party/google-maps/google-maps.service';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';

@Component({
  selector: 'app-new-project-general-info',
  templateUrl: './new-project-general-info.component.html',
  styleUrls: ['./new-project-general-info.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, GoogleMapsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class NewProjectGeneralInfoComponent implements AfterViewInit, OnChanges {
  @Input() formGroup!: FormGroup | null; // Formulario de la vivienda activa
  @Input() projectFormGroup!: FormGroup | null; // Formulario principal del proyecto
  @ViewChild('addressInput') addressInput!: ElementRef;

  // Variables para Google Maps
  searchAddress: string = '';
  showMap: boolean = false;
  generatedIframe: string = '';
  addressFieldsEnabled: boolean = false;
  googleMapsLoaded = false;
  googleMapsFailed = false;
  mapCenter: google.maps.LatLngLiteral = { lat: 40.4168, lng: -3.7038 }; // Madrid por defecto
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 20,
    minZoom: 8,
  };
  markerOptions: google.maps.MarkerOptions = {
    draggable: false,
  };

  // Variables para filas dinámicas
  additionalDateFields: any[] = [];
  additionalProfitabilityFields: any[] = [];

  // Solo aceptar selecciones del autocomplete cuando el usuario ha interactuado realmente.
  // Evita que el Web Component dispare un `gmp-select` espurio al inicializarse (que en
  // modo edición sobrescribía la dirección guardada con un lugar genérico tipo "Sol, Madrid").
  private userInteractedWithAutocomplete: boolean = false;


  constructor(
    private cdr: ChangeDetectorRef,
    private mapsLoader: GoogleMapsLoaderService,
    private mapsService: GoogleMapsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cuando cambia el formGroup (propiedad activa)
    if (changes['formGroup']) {
      // Al cambiar de propiedad activa, reseteamos el flag de interacción para que
      // un `gmp-select` espurio tras la carga no sobrescriba la dirección.
      this.userInteractedWithAutocomplete = false;
      if (this.formGroup) {
        this.loadPropertyData();
        this.loadAdditionalFieldsFromForm();
      }
    }
  }

  /**
   * Carga los datos de la propiedad activa en los campos del formulario
   */
  private loadPropertyData(): void {
    if (!this.formGroup) {
      this.resetAddressSection();
      return;
    }

    // Cargar dirección desde la propiedad
    const country = this.formGroup.get('country')?.value;
    const city = this.formGroup.get('city')?.value;
    const street = this.formGroup.get('street')?.value;
    const googleMapsIframe = this.formGroup.get('googleMapsIframe')?.value;

    // Construir dirección de búsqueda si hay datos
    if (country || city || street) {
      const addressParts = [street, city, country].filter(part => part);
      this.searchAddress = addressParts.join(', ') || '';
    } else {
      this.searchAddress = '';
    }

    this.addressFieldsEnabled = true;
    this.enableAddressFields();

    // Si hay iframe, mostrar mapa
    if (googleMapsIframe) {
      this.generatedIframe = googleMapsIframe;
      this.showMap = true;
      this.updateMapCenterFromIframe(googleMapsIframe);
      // Si el iframe legacy no contiene coordenadas (formato `?q=<dirección>`)
      // geocodificamos la dirección guardada para no dejar el mapa en el centro de Madrid.
      if (!this.iframeContainsCoordinates(googleMapsIframe) && this.searchAddress) {
        this.geocodeAddressAndCenter(this.searchAddress);
      }
    } else if (this.searchAddress) {
      // Hay datos de dirección sin iframe: mostrar mapa centrado en la dirección.
      this.showMap = true;
      this.generatedIframe = '';
      this.geocodeAddressAndCenter(this.searchAddress);
    } else {
      this.showMap = false;
      this.generatedIframe = '';
    }

    // Cargar descripción
    const description = this.formGroup.get('description')?.value;
    if (!description) {
      this.formGroup.get('description')?.setValue('');
    }

    this.cdr.detectChanges();
    this.applySearchAddressToAutocomplete();
  }

  /**
   * Refleja la dirección cargada en el input interno del Web Component
   * `<gmp-place-autocomplete>` para que el usuario vea la dirección ya existente.
   * El Web Component no acepta binding directo a `value`, así que se setea por DOM.
   */
  private applySearchAddressToAutocomplete(): void {
    if (!this.addressInput?.nativeElement) return;
    const apply = () => {
      const host = this.addressInput.nativeElement as HTMLElement;
      const innerInput = host.querySelector('input') as HTMLInputElement | null;
      if (innerInput) {
        innerInput.value = this.searchAddress || '';
      }
    };
    // El input interno puede no estar disponible inmediatamente tras el render
    // del Web Component; reintentamos en el siguiente tick.
    apply();
    setTimeout(apply, 0);
  }

  /**
   * Intenta extraer coordenadas del iframe para centrar el mapa
   */
  private updateMapCenterFromIframe(iframe: string): void {
    // Buscar coordenadas en el iframe (formato común: embed?pb=... o center=lat,lng)
    const centerMatch = iframe.match(/center=([^&]+)/);
    if (centerMatch) {
      const coords = centerMatch[1].split(',');
      if (coords.length === 2) {
        const lat = parseFloat(coords[0]);
        const lng = parseFloat(coords[1]);
        if (!isNaN(lat) && !isNaN(lng)) {
          this.mapCenter = { lat, lng };
        }
      }
    }
  }

  /**
   * Devuelve true si el iframe contiene coordenadas explícitas (parámetro `center=lat,lng`).
   * Los iframes generados con `maps.google.com/maps?q=<dirección>` no las llevan.
   */
  private iframeContainsCoordinates(iframe: string): boolean {
    return /center=-?\d+(\.\d+)?,-?\d+(\.\d+)?/.test(iframe);
  }

  /**
   * Geocodifica una dirección textual y centra el mapa en el resultado.
   */
  private geocodeAddressAndCenter(address: string): void {
    this.mapsLoader.load(['places'])
      .then(() => {
        if (!(window as any).google?.maps?.Geocoder) return;
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address }, (results: any[], status: string) => {
          if (status === 'OK' && results?.length) {
            const loc = results[0].geometry?.location;
            if (loc) {
              const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
              const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
              if (!isNaN(lat) && !isNaN(lng)) {
                this.mapCenter = { lat, lng };
                this.cdr.detectChanges();
              }
            }
          }
        });
      })
      .catch(() => { /* Google Maps no disponible: mantener el centro previo */ });
  }

  get promotoraControl(): FormControl | null {
    return this.projectFormGroup?.get('developer') as FormControl || null;
  }

  get descripcionControl(): FormControl | null {
    return this.formGroup?.get('description') as FormControl || null;
  }

  get paisControl(): FormControl | null {
    return this.formGroup?.get('country') as FormControl || null;
  }

  get estadoCCAAProvinciaControl(): FormControl | null {
    return this.formGroup?.get('stateRegion') as FormControl || null;
  }

  get localidadControl(): FormControl | null {
    return this.formGroup?.get('city') as FormControl || null;
  }

  get calleControl(): FormControl | null {
    return this.formGroup?.get('street') as FormControl || null;
  }

  get numeroCalleControl(): FormControl | null {
    return this.formGroup?.get('streetNumber') as FormControl || null;
  }

  get pisoControl(): FormControl | null {
    return this.formGroup?.get('floor') as FormControl || null;
  }

  get codigoPostalControl(): FormControl | null {
    return this.formGroup?.get('postalCode') as FormControl || null;
  }

  get googleMapsIframeControl(): FormControl | null {
    return this.formGroup?.get('googleMapsIframe') as FormControl || null;
  }

  get neighborhoodControl(): FormControl | null {
    return this.formGroup?.get('neighborhood') as FormControl || null;
  }

  // Getters para las características del inmueble
  get floorControl(): FormControl | null {
    return this.formGroup?.get('floor') as FormControl || null;
  }

  get roomsControl(): FormControl | null {
    return this.formGroup?.get('rooms') as FormControl || null;
  }

  get bathroomsControl(): FormControl | null {
    return this.formGroup?.get('bathrooms') as FormControl || null;
  }

  get exteriorControl(): FormControl | null {
    return this.formGroup?.get('exterior') as FormControl || null;
  }

  get elevatorControl(): FormControl | null {
    return this.formGroup?.get('elevator') as FormControl || null;
  }

  get squareMetersControl(): FormControl | null {
    return this.formGroup?.get('squareMeters') as FormControl || null;
  }

  // Getters para fechas clave
  get fechaLanzamientoControl(): FormControl | null {
    return this.formGroup?.get('launchDate') as FormControl || null;
  }

  get fechaCompraControl(): FormControl | null {
    return this.formGroup?.get('purchaseDate') as FormControl || null;
  }

  get fechaInicioReformaControl(): FormControl | null {
    return this.formGroup?.get('renovationStartDate') as FormControl || null;
  }

  get duracionEstimadaControl(): FormControl | null {
    return this.formGroup?.get('estimatedDuration') as FormControl || null;
  }

  get duracionRealControl(): FormControl | null {
    return this.formGroup?.get('actualDuration') as FormControl || null;
  }

  // Getters para rentabilidad
  get roiEstimadoControl(): FormControl | null {
    return this.formGroup?.get('estimatedROI') as FormControl || null;
  }

  get roiRealControl(): FormControl | null {
    return this.formGroup?.get('actualROI') as FormControl || null;
  }

  get tirEstimadaControl(): FormControl | null {
    return this.formGroup?.get('estimatedIRR') as FormControl || null;
  }

  get tirRealControl(): FormControl | null {
    return this.formGroup?.get('actualIRR') as FormControl || null;
  }

  ngAfterViewInit(): void {
    // Cargar datos de la propiedad activa si existe
    if (this.formGroup) {
      this.loadPropertyData();
      this.loadAdditionalFieldsFromForm();
    }
    
    // Esperar a que Google Maps esté cargado
    this.waitForGoogleMaps();
  }

  waitForGoogleMaps(): void {
    this.mapsLoader.load(['places'])
      .then(() => {
        this.googleMapsLoaded = true;
        this.markerOptions = { ...this.markerOptions, animation: google.maps.Animation.DROP };
        this.cdr.detectChanges();
        // El input interno del autocomplete solo existe tras la inicialización del Web Component
        this.applySearchAddressToAutocomplete();
      })
      .catch(() => {
        this.googleMapsFailed = true;
        this.enableManualMode();
      });
  }

  async onGmpPlaceSelected(event: any): Promise<void> {
    // Ignorar el evento si proviene de la inicialización del Web Component
    // (sin que el usuario haya tocado el buscador). De lo contrario, en modo edición
    // los campos cargados desde BD se sobrescribían por un lugar por defecto.
    if (!this.userInteractedWithAutocomplete) {
      return;
    }
    try {
      const placePrediction = event.placePrediction;
      if (!placePrediction) {
        this.googleMapsFailed = true;
        this.enableManualMode();
        return;
      }
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'addressComponents']
      });
      this.searchAddress = place.formattedAddress || '';
      this.autoCompleteFromNewPlace(place);
    } catch (error) {
      console.error('Error al procesar lugar seleccionado:', error);
      this.googleMapsFailed = true;
      this.enableManualMode();
    }
  }

  /**
   * Marca el autocomplete como interactuado por el usuario. Se llama desde el template
   * en (focusin)/(input) sobre el `<gmp-place-autocomplete>`.
   */
  onAutocompleteInteraction(): void {
    this.userInteractedWithAutocomplete = true;
  }

  private autoCompleteFromNewPlace(place: any): void {
    let streetNumber = '', route = '', locality = '', level1 = '', level2 = '', postalCode = '', country = '';
    let lat: number | null = null, lng: number | null = null;

    if (place.addressComponents) {
      for (const c of place.addressComponents) {
        const t = c.types;
        if (t.includes('street_number')) streetNumber = c.longText;
        if (t.includes('route')) route = c.longText;
        if (t.includes('locality')) locality = c.longText;
        if (t.includes('administrative_area_level_1')) level1 = c.longText;
        if (t.includes('administrative_area_level_2')) level2 = c.longText;
        if (t.includes('postal_code')) postalCode = c.longText;
        if (t.includes('country')) country = c.longText;
      }
    }
    if (place.location) {
      lat = typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat;
      lng = typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng;
    }

    this.paisControl?.setValue(country);
    this.estadoCCAAProvinciaControl?.setValue(level2 || level1);
    this.localidadControl?.setValue(locality || level2 || '');
    this.calleControl?.setValue(`${route} ${streetNumber}`.trim() || place.formattedAddress || '');
    this.codigoPostalControl?.setValue(postalCode);

    this.enableAddressFields();
    this.addressFieldsEnabled = true;

    if (lat !== null && lng !== null) {
      this.mapCenter = { lat, lng };
      const address = encodeURIComponent(place.formattedAddress || '');
      this.generatedIframe = this.mapsService.generateMapIframe(address);
      this.showMap = true;
      const iframeControl = this.formGroup?.get('googleMapsIframe');
      if (iframeControl) {
        iframeControl.setValue(this.generatedIframe);
        iframeControl.markAsTouched();
      }
    }
    this.cdr.detectChanges();
  }

  /**
   * Resetea toda la sección de dirección a su estado inicial
   */
  resetAddressSection(): void {
    this.clearAddressFields();
    this.disableAddressFields();
    this.addressFieldsEnabled = false;
    this.showMap = false;
    this.generatedIframe = '';
    
    // Limpiar el campo googleMapsIframe en el formulario de la propiedad
    const iframeControl = this.formGroup?.get('googleMapsIframe');
    if (iframeControl) {
      iframeControl.setValue('');
      iframeControl.markAsUntouched();
    }
    
    // Forzar detección de cambios
    this.cdr.detectChanges();
  }

  onPlaceSelected(place: google.maps.places.PlaceResult): void {
    console.log('Lugar seleccionado:', place);
    
    if (place.geometry && place.geometry.location) {
      const location = place.geometry.location;
      this.mapCenter = {
        lat: location.lat(),
        lng: location.lng()
      };
      
      this.showMap = true;
      this.addressFieldsEnabled = true; // Habilitar campos de dirección
      this.enableAddressFields(); // Habilitar campos usando FormControl
      
      // Generar iframe automáticamente utilizando el servicio centralizado
      const address = encodeURIComponent(place.formatted_address || '');
      this.generatedIframe = this.mapsService.generateMapIframe(address);
      
      // Auto-completar campos de dirección
      this.autoCompleteAddressFields(place);
      
      // Actualizar el campo googleMapsIframe en el formulario de la propiedad
      const iframeControl = this.formGroup?.get('googleMapsIframe');
      if (iframeControl) {
        iframeControl.setValue(this.generatedIframe);
        iframeControl.markAsTouched();
      }
      
      // Actualizar el campo de búsqueda con la dirección formateada
      this.searchAddress = place.formatted_address || '';
      
      console.log('Iframe generado:', this.generatedIframe);
      
      // Forzar detección de cambios
      this.cdr.detectChanges();
    } else {
      console.warn('No se pudo obtener la geometría del lugar seleccionado');
      // Si no hay geometría, habilitar modo manual
      this.enableManualMode();
    }
  }

  autoCompleteAddressFields(place: google.maps.places.PlaceResult): void {
    const addressComponents = place.address_components || [];
    
    // Limpiar campos primero
    this.paisControl?.setValue('');
    this.estadoCCAAProvinciaControl?.setValue('');
    this.localidadControl?.setValue('');
    this.calleControl?.setValue('');
    this.numeroCalleControl?.setValue('');
    this.pisoControl?.setValue('');
    this.codigoPostalControl?.setValue('');
    
    // Auto-completar campos
    addressComponents.forEach(component => {
      const types = component.types;
      
      if (types.includes('country')) {
        this.paisControl?.setValue(component.long_name);
      }
      
      if (types.includes('administrative_area_level_1')) {
        this.estadoCCAAProvinciaControl?.setValue(component.long_name);
      }
      
      if (types.includes('locality')) {
        this.localidadControl?.setValue(component.long_name);
      } else if (types.includes('administrative_area_level_2') && !this.localidadControl?.value) {
        this.localidadControl?.setValue(component.long_name);
      }
      
      if (types.includes('route')) {
        this.calleControl?.setValue(component.long_name);
      } else if (types.includes('street_address') && !this.calleControl?.value) {
        this.calleControl?.setValue(component.long_name);
      }
      
      if (types.includes('street_number')) {
        this.numeroCalleControl?.setValue(component.long_name);
      }
      
      if (types.includes('postal_code')) {
        this.codigoPostalControl?.setValue(component.long_name);
      }
    });
    
    // Si no se encontró calle, usar la dirección formateada
    if (!this.calleControl?.value && place.formatted_address) {
      this.calleControl?.setValue(place.formatted_address);
    }
    
    // Extraer número y piso de la dirección formateada si no se encontraron por separado
    if (place.formatted_address && (!this.numeroCalleControl?.value || !this.pisoControl?.value)) {
      this.extractNumberAndFloorFromAddress(place.formatted_address);
    }
  }

  /**
   * Extrae el número de calle y piso de una dirección formateada
   */
  extractNumberAndFloorFromAddress(address: string): void {
    // Patrones para extraer número y piso
    const patterns = [
      // Patrón: Calle, 123, 4ºA
      /^([^,]+),\s*(\d+)(?:,\s*([^,]+))?/,
      // Patrón: Calle 123, 4ºA
      /^([^,]+)\s+(\d+)(?:,\s*([^,]+))?/,
      // Patrón: Calle, 123 4ºA
      /^([^,]+),\s*(\d+)\s+([^,]+)/,
      // Patrón: Calle 123 4ºA
      /^([^,]+)\s+(\d+)\s+([^,]+)/
    ];

    for (const pattern of patterns) {
      const match = address.match(pattern);
      if (match) {
        const [, street, number, floor] = match;
        
        // Si no tenemos calle, la establecemos
        if (!this.calleControl?.value && street) {
          this.calleControl?.setValue(street.trim());
        }
        
        // Si no tenemos número, lo establecemos
        if (!this.numeroCalleControl?.value && number) {
          this.numeroCalleControl?.setValue(number.trim());
        }
        
        // Si no tenemos piso, lo establecemos
        if (!this.pisoControl?.value && floor) {
          this.pisoControl?.setValue(floor.trim());
        }
        
        break;
      }
    }
  }

  /**
   * Limpia todos los campos de dirección
   */
  clearAddressFields(): void {
    this.paisControl?.setValue('');
    this.estadoCCAAProvinciaControl?.setValue('');
    this.localidadControl?.setValue('');
    this.calleControl?.setValue('');
    this.numeroCalleControl?.setValue('');
    this.pisoControl?.setValue('');
    this.codigoPostalControl?.setValue('');
  }

  /**
   * Deshabilita todos los campos de dirección
   */
  disableAddressFields(): void {
    this.paisControl?.disable();
    this.estadoCCAAProvinciaControl?.disable();
    this.localidadControl?.disable();
    this.calleControl?.disable();
    this.numeroCalleControl?.disable();
    this.pisoControl?.disable();
    this.codigoPostalControl?.disable();
  }

  /**
   * Habilita todos los campos de dirección
   */
  enableAddressFields(): void {
    this.paisControl?.enable();
    this.estadoCCAAProvinciaControl?.enable();
    this.localidadControl?.enable();
    this.calleControl?.enable();
    this.numeroCalleControl?.enable();
    this.pisoControl?.enable();
    this.codigoPostalControl?.enable();
  }

  toggleCharacteristic(characteristic: string) {
    const control = this.formGroup?.get(characteristic);
    if (control) {
      if (characteristic === 'exterior' || characteristic === 'elevator') {
        // Para checkboxes booleanos
        control.setValue(!control.value);
      } else {
        // Para campos de texto/número, abrir modal o input
        this.openCharacteristicInput(characteristic);
      }
    }
  }

  openCharacteristicInput(characteristic: string) {
    // Aquí puedes implementar un modal o input inline para capturar el valor
    const currentValue = this.formGroup?.get(characteristic)?.value;
    const promptValue = prompt(`Introduce el valor para ${characteristic}:`, currentValue || '');
    
    if (promptValue !== null) {
      this.formGroup?.get(characteristic)?.setValue(promptValue);
    }
  }

  addNewCharacteristic() {
    // Aquí puedes implementar la lógica para agregar nuevas características
    console.log('Agregar nueva característica');
  }

  /**
   * Habilita el modo manual cuando Google Maps no está disponible
   */
  private enableManualMode(): void {
    console.log('Habilitando modo manual para entrada de dirección');
    this.enableAddressFields();
    this.addressFieldsEnabled = true;
  }

  // Métodos para manejar filas dinámicas
  addDateField(): void {
    const newField = {
      id: Date.now().toString(),
      label: 'Nueva fecha',
      type: 'date',
      value: null
    };
    this.additionalDateFields.push(newField);
    this.saveAdditionalFieldsToForm();
    this.cdr.detectChanges();
  }

  addProfitabilityField(): void {
    const newField = {
      id: Date.now().toString(),
      label: 'Nuevo campo',
      type: 'number',
      value: null
    };
    this.additionalProfitabilityFields.push(newField);
    this.saveAdditionalFieldsToForm();
    this.cdr.detectChanges();
  }

  removeDateField(index: number): void {
    this.additionalDateFields.splice(index, 1);
    this.saveAdditionalFieldsToForm();
    this.cdr.detectChanges();
  }

  removeProfitabilityField(index: number): void {
    this.additionalProfitabilityFields.splice(index, 1);
    this.saveAdditionalFieldsToForm();
    this.cdr.detectChanges();
  }

  onDateFieldChange(field: any, event: any): void {
    field.value = event.target.value;
    this.saveAdditionalFieldsToForm();
  }

  onProfitabilityFieldChange(field: any, event: any): void {
    field.value = event.target.value;
    this.saveAdditionalFieldsToForm();
  }

  onDateFieldLabelChange(field: any, event: any): void {
    field.label = event.target.value;
    this.saveAdditionalFieldsToForm();
  }

  onProfitabilityFieldLabelChange(field: any, event: any): void {
    field.label = event.target.value;
    this.saveAdditionalFieldsToForm();
  }

  /**
   * Guarda los campos adicionales dinámicos en el FormGroup
   */
  private saveAdditionalFieldsToForm(): void {
    if (!this.formGroup) return;

    // Asegurar que los controles existan
    if (!this.formGroup.get('keyDatesAdditionalFields')) {
      this.formGroup.addControl('keyDatesAdditionalFields', new FormControl([]));
    }
    if (!this.formGroup.get('profitabilityAdditionalFields')) {
      this.formGroup.addControl('profitabilityAdditionalFields', new FormControl([]));
    }

    // Guardar los campos adicionales
    this.formGroup.get('keyDatesAdditionalFields')?.setValue([...this.additionalDateFields]);
    this.formGroup.get('profitabilityAdditionalFields')?.setValue([...this.additionalProfitabilityFields]);
  }

  /**
   * Carga los campos adicionales dinámicos desde el FormGroup
   */
  private loadAdditionalFieldsFromForm(): void {
    if (!this.formGroup) return;

    const keyDatesFields = this.formGroup.get('keyDatesAdditionalFields')?.value || [];
    const profitabilityFields = this.formGroup.get('profitabilityAdditionalFields')?.value || [];

    if (Array.isArray(keyDatesFields) && keyDatesFields.length > 0) {
      this.additionalDateFields = [...keyDatesFields];
    }

    if (Array.isArray(profitabilityFields) && profitabilityFields.length > 0) {
      this.additionalProfitabilityFields = [...profitabilityFields];
    }
  }

}
